/**
 * Destination à restaurer après le login.
 *
 * Un lien profond ouvert HORS connexion (cockpit `/animator/...` scanné, bouton
 * « Lancer <jeu> ce soir » d'un email de relance → `/app/<clientID>?game=X`,
 * lien « Plutôt faire une pause ? » → `/account/cancel`) tombe sur le catch-all
 * d'AuthRoutes, qui renvoie vers /signin en jetant l'URL demandée. On la mémorise
 * donc dans `sessionStorage` (AuthRoutes) et on la consomme une seule fois après
 * le login (MainRoutes, route `/`).
 *
 * Règles :
 * - liste blanche de préfixes : `/animator`, `/app/<clientID>`, `/account/cancel`,
 *   `/account/keep` (bouton « Garder Unboared » de l'email de fin d'essai),
 *   `/account/annual` (bouton « Passer à l'annuel » de l'email d'offre annuelle),
 *   `/account/brand` (retour du portail Stripe après « Passer en Pro ») ;
 *   tout le reste (`/`, `/signin`, `/home`, `/app/demo`...) n'est pas mémorisé,
 *   ce qui évite toute boucle de redirection ;
 * - la valeur relue est revalidée (le stockage est modifiable par n'importe quel
 *   script de l'origine) : chemin interne uniquement, pas de `//`, pas de `\`,
 *   pas de schéma → sinon ignorée (pas d'open redirect) ;
 * - tout accès au stockage est gainé try/catch, y compris l'accès à la propriété
 *   `sessionStorage` elle-même qui peut jeter (navigation privée stricte, données
 *   de site bloquées) : jamais d'écran blanc (gotcha #116).
 */

export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

/** Longueur max acceptée (une URL de console légitime fait < 200 caractères). */
const MAX_LENGTH = 2048;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Getter (et non l'objet) : l'accès même à `window.sessionStorage` peut jeter. */
type StorageGetter = () => StorageLike;

const defaultStorage: StorageGetter = () => window.sessionStorage;

/** `/animator`, `/animator/...` */
const ANIMATOR_RE = /^\/animator(\/|$)/;
/** `/app/<clientID>` (un seul segment, comme la route `app/:clientID`). */
const APP_RE = /^\/app\/([^/]+)\/?$/;
/** `/account/cancel`, `/account/keep`, `/account/annual` (liens des emails), `/account/brand` (retour du portail) */
const ACCOUNT_CANCEL_RE = /^\/account\/(cancel|keep|annual|brand)\/?$/;

/** Le chemin (sans query) mérite-t-il d'être restauré après le login ? */
export function shouldRememberPath(pathname: string): boolean {
    if (ANIMATOR_RE.test(pathname) || ACCOUNT_CANCEL_RE.test(pathname)) return true;
    const app = APP_RE.exec(pathname);
    // `/app/demo` est une route publique servie hors login : rien à restaurer.
    return !!app && app[1] !== 'demo';
}

/** Valide une destination relue du stockage. Renvoie `null` si elle est refusée. */
export function sanitizePostLoginRedirect(value: unknown): string | null {
    if (typeof value !== 'string' || value.length === 0 || value.length > MAX_LENGTH) return null;
    // Chemin interne strict : commence par un seul `/`, aucun antislash (les
    // navigateurs le traitent comme `/` → `/\evil.com` = `//evil.com`), aucun
    // caractère de contrôle ni espace.
    if (!value.startsWith('/') || value.startsWith('//')) return null;
    // eslint-disable-next-line no-control-regex
    if (/[\\\s\u0000-\u001f\u007f]/.test(value)) return null;
    const cut = value.search(/[?#]/);
    const pathname = cut === -1 ? value : value.slice(0, cut);
    // La liste blanche exclut de fait tout schéma (`https:`, `javascript:`).
    return shouldRememberPath(pathname) ? value : null;
}

/**
 * Mémorise `pathname + search` si le chemin est dans la liste blanche.
 * N'écrase rien sinon (un passage par /signin ne doit pas effacer la cible).
 */
export function rememberPostLoginRedirect(
    location: { pathname: string; search: string },
    getStorage: StorageGetter = defaultStorage
): void {
    const target = sanitizePostLoginRedirect(location.pathname + location.search);
    if (!target) return;
    try {
        getStorage().setItem(POST_LOGIN_REDIRECT_KEY, target);
    } catch {
        /* Stockage indisponible : pas de mémorisation, on retombe sur le défaut après login. */
    }
}

/** Lit ET supprime la destination mémorisée (une seule fois), validée. */
export function consumePostLoginRedirect(getStorage: StorageGetter = defaultStorage): string | null {
    let stored: string | null = null;
    try {
        const storage = getStorage();
        stored = storage.getItem(POST_LOGIN_REDIRECT_KEY);
        if (stored !== null) storage.removeItem(POST_LOGIN_REDIRECT_KEY);
    } catch {
        /* Stockage indisponible : on ignore. */
        return null;
    }
    return sanitizePostLoginRedirect(stored);
}
